import styles from './EditableCell.module.scss';

import { TextareaAutosize } from '@mui/material';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellProps } from 'react-table';

import {
  formatAsFloat,
  formatInputValueAsFloat,
  parseAsFloat,
} from '@work4all/utils';
import { useForceUpdate } from '@work4all/utils/lib/hooks/use-force-update';

import { CheckboxRadioItem } from '../../../../../../input/checkbox-radio-item';
import { Unit } from '../../../../../../input/labeled-currency-input/components/unit/Unit';
import { EditModeCell } from '../../../../plugins/useEditMode';
import { ExtendedNumberCell } from '../../../../utils';

export interface EditableCellNumericProps {
  min?: number;
  max?: number;
  step?: number;
  transform?: (
    input: string | number | DateTime<true> | DateTime<false> | boolean
  ) => string | number | DateTime<true> | DateTime<false> | boolean;
  transformInputValue?: (input: string) => string;
  unit?: string;
}

interface EditableCellUserProps extends EditableCellNumericProps {
  onSubmit?: () => void;
  onExit?: (
    resultValue: string | number | boolean | DateTime<true> | DateTime<false>
  ) => void;
  onNext?: (
    event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    dir: 1 | -1
  ) => void;
  onChange?: (input: string | boolean) => void;
  onSelectionChange?: (start?: number, end?: number) => void;
  selectinRange: { start: number; end: number };
  autoFocus?: boolean;
  textarea?: boolean;
  type?: string;
  disabled?: boolean;
  isValueVisble?: boolean;
  isVisible: boolean;
  maxHeight: number;
  showZeros?: boolean;
  shrinked?: boolean;
  isEditMode?: boolean;
  isEditable?: boolean;
  maxLines?: number;
  maxLength?: number;
  stopPropagation?: boolean;
  noBorder?: boolean;
}

export interface EditableCellProps
  extends CellProps<Record<string, unknown>>,
    EditableCellUserProps {
  cell: EditModeCell & ExtendedNumberCell;
}

const resultWithZero = (value: string | number, result: string | number) => {
  return result;
};

const resultWithoutZero = (value: string | number, result: string | number) =>
  value?.toString() === '0' ? ' ' : result;

const dateTransform = (value: string) => {
  return DateTime.fromISO(value).toFormat('yyyy-MM-dd');
};

export const EditableCell: React.FC<Partial<EditableCellProps>> = (
  props: EditableCellProps
) => {
  const initialConvert = props.showZeros ? resultWithZero : resultWithoutZero;

  const {
    i18n: { language },
  } = useTranslation();

  const convert = () => {
    if (props.type === 'date') return dateTransform(props.value);
    return props.type === 'number'
      ? initialConvert(
          props.value,
          formatAsFloat(props.value, {
            locale: language,
            isFinished: true,
            ...props.cell.column.cellParams,
            ...props.numberCellProps,
          })
        )
      : props.value;
  };

  const initialValue = convert();

  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState(initialValue);

  const { autoFocus, onEdit } = props.cell.getEditableCellProps?.() ?? {
    onEdit: () => {},
  };

  function transformBeforeExit(value: string) {
    const action = () => {
      if (props.type === 'date') {
        if (!value) return value;
        return DateTime.fromFormat(value, 'yyyy-MM-dd');
      }
      if (props.type === 'time') {
        return DateTime.fromFormat(value, 'HH:mm');
      }
      if (props.type === 'number') {
        const resultValue = parseAsFloat(value, language) || 0;
        return resultValue;
      }

      return value;
    };

    const result = action();
    if (props.transform) {
      return props.transform(result);
    }
    return result;
  }

  const setSelection = props.onSelectionChange
    ? (
        event: null | {
          currentTarget: { selectionStart: number; selectionEnd: number };
        }
      ) => {
        if (!event) {
          props.onSelectionChange();
        } else if (
          event.currentTarget?.selectionStart &&
          event.currentTarget?.selectionEnd
        )
          props.onSelectionChange(
            event.currentTarget.selectionStart,
            event.currentTarget.selectionEnd
          );
      }
    : undefined;

  useEffect(() => {
    if (props.type === 'date') {
      const transformed = DateTime.fromISO(initialValue).toFormat('yyyy-MM-dd');
      setValue(transformed);
    }
    if (props.type === 'time') {
      const transformed = DateTime.fromISO(initialValue).toLocaleString(
        DateTime.TIME_SIMPLE
      );
      setValue(transformed);
    } else if (props.type === 'number') {
      const currentValueParsed = parseAsFloat(value, language);
      if (props.value === currentValueParsed) {
        return;
      }
      const floatValue = formatAsFloat(initialValue, {
        locale: language,
        isFinished: true,
        ...props.cell.column.cellParams,
        ...props.numberCellProps,
      });
      if (!initialValue || !initialValue.trim()) {
        setValue(' ');
      } else setValue(floatValue);
    } else {
      setValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.cell.column.cellParams, props.type, initialValue]);

  const onChange = (event) => {
    let value = event.target.value;
    if (props.type === 'text' && props.maxLines) {
      value = value.split('\n').slice(0, props.maxLines).join('\n');
    }
    if (props.type === 'number') {
      value = formatInputValueAsFloat(event.currentTarget, {
        locale: language,
        ...props.cell.column.cellParams,
        ...props.numberCellProps,
      });
      if (props.transformInputValue) {
        value = props.transformInputValue(value);
      }
    }
    setValue(value);
    const resultValue = transformBeforeExit(value);
    onEdit(resultValue, 'update');
    props.onChange?.(value);
  };

  const [isFocused, setIsFocused] = useState(false);

  function onBlur(event) {
    if (props.type === 'number') {
      const result = formatInputValueAsFloat(event.currentTarget, {
        locale: language,
        isFinished: true,
        ...props.cell.column.cellParams,
        ...props.numberCellProps,
      });
      setValue(result);
    }
    setSelection?.(null);
    const resultValue = transformBeforeExit(value);
    setIsFocused(false);
    if (props.onNext) {
      onEdit(resultValue, 'blur-update');
      return;
    }
    onEdit(resultValue, 'blur');
    props.onExit?.(resultValue);
  }

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (setSelection) setTimeout(() => setSelection(event), 0);
    switch (event.key) {
      case 'Tab': {
        const resultValue = transformBeforeExit(value);
        event.stopPropagation();
        onEdit(resultValue, 'update');
        props.onNext?.(event, event.shiftKey ? -1 : 1);
        break;
      }
      case 'Enter': {
        const resultValue = transformBeforeExit(value);
        if (props.type === 'number') {
          event.preventDefault();
          event.stopPropagation();
          onEdit(resultValue, 'update');
          props.onExit?.(resultValue);
        } else if (props.textarea && (event.ctrlKey || event.metaKey)) {
          // Handle Ctrl+Enter (or Cmd+Enter on Mac) for new line in textarea
          event.preventDefault();
          const textarea = event.currentTarget as HTMLTextAreaElement;
          const { selectionStart, selectionEnd, value } = textarea;

          // Insert a newline at the current caret/selection
          textarea.setRangeText(
            '\n',
            selectionStart,
            selectionEnd,
            'end' // moves caret after the newline
          );

          // Trigger input event so any listeners update
          textarea.dispatchEvent(new Event('input', { bubbles: true }));

          // Ensure browser scrolls naturally
          requestAnimationFrame(() => {
            textarea.scrollTop = textarea.scrollHeight;
          });
        }
        break;
      }
    }
  };
  const onFocusHandle = (target: HTMLTextAreaElement | HTMLInputElement) => {
    if (props.selectinRange) {
      target.setSelectionRange(
        props.selectinRange.start,
        props.selectinRange.end
      );
      return;
    }
    target.select();
    setIsFocused(true);
  };

  const onFocus = (e) => {
    onFocusHandle(e.currentTarget);
    setSelection?.(e);
  };
  const focusOnVisibleCell = autoFocus && props.isVisible;

  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const innerProps = {
    autoFocus: props.autoFocus ?? focusOnVisibleCell,
    value:
      props.isValueVisble || props.isValueVisble === undefined
        ? (props.shrinked ||
            (!props.isEditMode && props.rowSizeMode !== 'AUTO')) &&
          props.type === 'text'
          ? truncateInputText(
              inputRef as React.RefObject<HTMLTextAreaElement>,
              value
            )
          : value
        : '',
    onChange,
    onBlur,
    onKeyDown,
    onFocus,
    type: props.type === 'number' ? 'text' : props.type,
    readOnly:
      props.disabled ||
      props.shrinked ||
      !props.isEditable ||
      !props.isEditMode,
    maxLength: props.maxLength,
  };

  const manualFocus = useRef(false);
  useEffect(() => {
    setTimeout(() => {
      if (
        !isFocused &&
        innerProps.autoFocus &&
        inputRef.current &&
        !manualFocus.current
      ) {
        manualFocus.current = true;
        onFocusHandle(inputRef.current);
      }
    }, 0);
  }, [isFocused, innerProps.autoFocus]);

  const inputStrictHandlers = {
    onSelect: setSelection,
    onInput: setSelection,
    onMouseDown: setSelection,
  };
  const forceUpdate = useForceUpdate();
  useEffect(() => {
    if (inputRef.current) forceUpdate();
  }, []);

  if (props.type === 'checkbox') {
    return (
      <CheckboxRadioItem
        checked={value}
        onChange={(e) => {
          if (props.isEditMode) {
            const resultValue = e.target.checked;
            if (props.onNext) {
              onEdit(resultValue, 'blur-update');
              return;
            }
            onEdit(resultValue, 'blur');
            props.onExit?.(resultValue);
          }
        }}
        label=""
        horizontalPadding={false}
        verticalPadding={false}
        disabled={!props.isEditMode}
        className={clsx({ [styles.checkboxActive]: value })}
      />
    );
  } else if (props.textarea) {
    return (
      <div
        className={clsx(styles.input, {
          [styles.focused]: isFocused,
          [styles.inputPointers]: !props.stopPropagation,
          [styles.noBorder]:
            !props.isEditMode ||
            props.shrinked ||
            !props.isEditable ||
            props.noBorder,
        })}
      >
        <TextareaAutosize
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          {...innerProps}
          {...inputStrictHandlers}
          className={clsx(styles.innerInput, styles.inputPadding, {
            // Pixel perfect, we lose somewhere 0.5px only when fontSize is 14px
            [styles.inputPaddingFix]:
              document.documentElement.style.fontSize === '14px',
            [styles.multiLine]: value?.split('\n').length > 1,
            [styles.disabled]: !props.isEditable,
          })}
          // Important that shrinked has a value and is not undefined
          minRows={
            props.maxLines ? props.maxLines : props.shrinked === false ? 4 : 1
          }
          style={{
            resize: 'none',
            maxHeight: props.maxHeight,
            overflowY: 'auto',
            ...props.css,
          }}
        />
      </div>
    );
  } else {
    return (
      <div
        className={clsx(styles.input, styles.inputPaddingText, {
          [styles.focused]: isFocused,
          [styles.inputPointers]: !props.stopPropagation,
          [styles.noBorder]:
            !props.isEditMode ||
            props.shrinked ||
            !props.isEditable ||
            props.noBorder,
        })}
      >
        {props.dropdown && props.dropdown(props)}
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          value={value} // Make sure value is explicitly passed for controlled component
          {...innerProps}
          // type={props.type} // Pass type to the input
          className={clsx(styles.innerInput, {
            [styles.number]: props.type === 'number',
            [styles.disabled]: !props.isEditable,
          })}
          style={props.css}
          max={props.max}
          min={props.min}
          step={props.step}
          // inputProps from MUI is translated directly to attributes for native input
        />
        {props.unit && <Unit unit={props.unit} />}
      </div>
    );
  }
};

function truncateInputText(
  areaRef: React.RefObject<HTMLTextAreaElement>,
  text = ''
): string {
  if (typeof text !== 'string') return text;
  if (!text) return text;
  if (!areaRef.current) return '';

  const input = areaRef.current;
  const font = window.getComputedStyle(input).font;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return '';

  context.font = font;

  const ellipsis = '...';

  // Calculate maxWidth as 90% of textarea width
  const maxWidth = input.clientWidth * 0.9;

  // Calculate max visible rows based on height and line-height
  const lineHeight = parseFloat(window.getComputedStyle(input).lineHeight);
  const parentCell = input.closest('[role="cell"]'); // Find parent cell
  if (!parentCell) return text;

  const maxRows = Math.floor(parentCell.clientHeight / lineHeight);

  const rawLines = text.split('\n'); // Keep original lines

  let truncatedText = '';
  let currentRows = 0;
  let finalText = '';

  for (const rawLine of rawLines) {
    const words = rawLine.split(' '); // Split line into words
    let newLine = '';

    for (const word of words) {
      const testLine = newLine + (newLine ? ' ' : '') + word;
      const testWidth = context.measureText(testLine).width;

      if (testWidth > maxWidth) {
        if (currentRows + 1 >= maxRows) {
          if (finalText) {
            return finalText + ellipsis;
          }
          return truncatedText + newLine.trim() + ellipsis; // Truncate last visible line
        }
        truncatedText += newLine + '\n'; // Keep original line breaks
        newLine = word;
        currentRows++;
      } else {
        newLine = testLine;
      }
    }
    currentRows++;
    truncatedText += newLine;

    if (currentRows < maxRows) {
      truncatedText += '\n';
    } else if (!finalText) {
      finalText = truncatedText.trim();
    }
  }
  if (!finalText) return truncatedText.trim();

  // Calculate all rows to decide if we need ellipsis
  if (currentRows <= maxRows) {
    return finalText;
  } else {
    return finalText + ellipsis;
  }
}
